import type { TFunction } from 'i18next';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { getFormatedSize, getTestProps } from '../../lib/helpers';

import { useSpacePlanHistory } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';

import Loader from '../Loader/Loader';
import Pagination from '../Pagination/Pagination';
import Panel from '../Panel/Panel';
import { getDetailsMap } from './getDetailsMap';

import './plan-history-table.css';

const CURRENT_PLAN_PARAMS = {
  limit: 1,
  filters: JSON.stringify({ current: { type: 'equals', filter: true } }),
};

const getPropertyFormat = (
  property: string | boolean,
  t: TFunction,
  isDate = false,
  mode?: string,
) => {
  if (typeof property === 'boolean') return property.toString();
  if (isDate) return moment(property).format('L LT');
  return (
    getFormatedSize(property, t('Global.Unlimited'), mode) || 'N/A'
  ).toString();
};

interface PlanHistoryModalContentProps {
  /**
   * Space id
   */
  spaceId?: string;
  /**
   * Test id for component
   */
  testId?: string;
}

const PlanHistoryModalContent = ({
  spaceId,
  testId = '',
}: PlanHistoryModalContentProps) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const {
    data: currentPlan,
    isLoading: currentPlanIsLoading,
    errors: currentPlanErrors,
  } = useSpacePlanHistory(spaceId, CURRENT_PLAN_PARAMS);

  const planHistoryParams = useMemo(
    () => ({
      limit: 10,
      page,
    }),
    [page],
  );

  const {
    data: planHistory,
    isLoading: planHistoryIsLoading,
    errors: planHistoryErrors,
    pagination,
  } = useSpacePlanHistory(spaceId, planHistoryParams);

  useApiErrorsToast(planHistoryErrors);
  useApiErrorsToast(currentPlanErrors);

  const informationBlock = useMemo(
    () => [
      {
        key: 'name',
        name: t('PlanHistory.CurrentPlan'),
        value: currentPlan[0]?.planCopy?.name,
      },
      {
        key: 'changed',
        name: t('PlanHistory.LastChange'),
        value: currentPlan[0]?.createdAt
          ? moment(currentPlan[0].createdAt).format('L LT')
          : 'N/A',
      },
    ],
    [currentPlan, t],
  );

  const planLimitsDetails = useMemo(() => getDetailsMap(t), [t]);

  return planHistoryIsLoading || currentPlanIsLoading ? (
    <div className="flex justify-center items-center h-24">
      <Loader type="spinner-grid" size="small" />
    </div>
  ) : (
    <>
      <div className="pb-2" {...getTestProps(testId, 'information')}>
        {informationBlock.map((info) => (
          <div key={info.key}>
            <strong>{info.name}</strong>: {info.value}
          </div>
        ))}
      </div>
      {planHistory.map(({ id, notes, plan, planCopy, current, createdAt }) => (
        <Panel
          key={id}
          title={createdAt ? moment(createdAt).format('L LT') : 'N/A'}
          additionalContainerClasses={
            'px-0 py-3 bg-transparent dark:bg-transparent border-t rounded-none'
          }
          {...getTestProps(testId, id, 'testId')}
        >
          <p>
            <strong>{t('PlanHistory.Current')}: </strong>
            {getPropertyFormat(current, t, true)}
          </p>
          {notes && (
            <p className="mt-2">
              <strong>{t('PlanHistory.Notes')}: </strong>
              {notes}
            </p>
          )}
          <table className="plan-history-table mt-4">
            <thead>
              <tr>
                <td>{t('PlanHistory.Property')}</td>
                <td>{t('PlanHistory.UserValue')}</td>
                <td>{t('PlanHistory.PlanValue')}</td>
              </tr>
            </thead>
            <tbody>
              {planLimitsDetails.map(
                ({ planCopyProperty, planProperty, label, isDate, mode }) => {
                  const planCopyValue = getPropertyFormat(
                    planCopy[planCopyProperty],
                    t,
                    isDate,
                    mode,
                  );
                  const planValue = getPropertyFormat(
                    plan[planProperty],
                    t,
                    isDate,
                    mode,
                  );

                  return (
                    <tr key={planCopyProperty}>
                      <td>{label}</td>
                      <td
                        className={
                          planCopyValue !== planValue ? 'text-red' : ''
                        }
                        {...getTestProps(
                          testId,
                          `${id}-plan-copy-${planCopyProperty}`,
                        )}
                      >
                        {planCopyValue}
                      </td>
                      <td
                        {...getTestProps(
                          testId,
                          `${id}-plan-${planCopyProperty}`,
                        )}
                      >
                        {planValue}
                      </td>
                    </tr>
                  );
                },
              )}
            </tbody>
          </table>
        </Panel>
      ))}
      {!!pagination?.total_pages && pagination?.total_pages > 1 && (
        <Pagination
          numOfPages={pagination.total_pages}
          page={page}
          onPageChange={setPage}
          additionalClasses="p-0!"
          {...getTestProps(testId, 'pagination', 'testId')}
        />
      )}
    </>
  );
};

export default PlanHistoryModalContent;
